import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  AppointmentMode,
  useClinicAppointmentSettings,
} from '@/hooks/useAppointmentSetting'
import { appointmentService } from '@/services/appointment_service'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

type Props = {
  clinicId?: number
}

/**
 * 診所層級的預約模式切換卡片。
 * - DEFAULT：每個診間各自有一份 setting
 * - SHARED_ROOMS：診所內所有診間共用同一份預約清單，由前端派發
 *
 * 切換邏輯：
 * - 若診所已有 SHARED setting → 顯示 SHARED（取該 setting 操作切換）
 * - 否則顯示 DEFAULT（取第一個 active setting 作為切換基準）
 * - 診所完全沒有 setting：提示請先至任一診間建立預約掛號設定
 */
export function ClinicAppointmentModeCard({ clinicId }: Props) {
  const { token } = useAuth()
  const { settings, mutate } = useClinicAppointmentSettings(clinicId)

  const [pendingModeChange, setPendingModeChange] = useState<AppointmentMode | null>(null)
  const [isSwitching, setIsSwitching] = useState(false)

  // 決定目前模式與要操作的 setting
  const sharedSetting = settings.find((s) => s.appointment_mode === 'SHARED_ROOMS')
  const firstDefaultSetting = settings.find((s) => s.appointment_mode === 'DEFAULT')
  const currentMode: AppointmentMode = sharedSetting ? 'SHARED_ROOMS' : 'DEFAULT'
  const operationSetting = sharedSetting ?? firstDefaultSetting
  const sharedRoomCount = sharedSetting?.examination_rooms?.length ?? 0

  const handleModeChange = (newMode: AppointmentMode) => {
    if (newMode === currentMode) return
    if (!operationSetting) {
      toast.error('診所尚未有任何預約掛號設定，請先到任一診間建立設定後再切換模式')
      return
    }
    setPendingModeChange(newMode)
  }

  const confirmModeChange = async () => {
    if (!pendingModeChange || !operationSetting || !token) return
    setIsSwitching(true)
    try {
      const response = await appointmentService.switchMode(
        operationSetting.appointment_setting_id,
        pendingModeChange,
        token,
      )
      if (response.success) {
        toast.success(
          pendingModeChange === 'SHARED_ROOMS' ? '已切換為診間共用模式' : '已切換為各診間獨立模式',
        )
        mutate()
      } else {
        toast.error('模式切換失敗', { description: response.error?.message })
      }
    } catch (err) {
      toast.error('模式切換失敗', {
        description: err instanceof Error ? err.message : undefined,
      })
    } finally {
      setIsSwitching(false)
      setPendingModeChange(null)
    }
  }

  if (!clinicId) return null

  return (
    <>
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>預約模式</CardTitle>
          <CardDescription>
            ⚠️ 此設定影響整個診所的所有診間，切換會重新分配預約資料
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={currentMode}
            onValueChange={(val) => handleModeChange(val as AppointmentMode)}
            className="flex flex-col space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="DEFAULT" id="clinic-mode-default" />
              <Label htmlFor="clinic-mode-default" className="font-normal cursor-pointer">
                各診間獨立預約清單（預設）
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="SHARED_ROOMS" id="clinic-mode-shared" />
              <Label htmlFor="clinic-mode-shared" className="font-normal cursor-pointer">
                診間共用預約清單
              </Label>
            </div>
          </RadioGroup>
          {currentMode === 'SHARED_ROOMS' && (
            <p className="mt-3 text-sm text-muted-foreground">
              目前診所內 {sharedRoomCount} 間診間共用此預約清單
            </p>
          )}
          {!operationSetting && (
            <p className="mt-3 text-sm text-amber-600">
              診所尚未有任何預約掛號設定。請先到任一診間的「預約掛號設定」建立設定後，才能切換模式。
            </p>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={pendingModeChange !== null}
        onOpenChange={(open) => !open && setPendingModeChange(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingModeChange === 'SHARED_ROOMS'
                ? '切換為「診間共用預約清單」？'
                : '切換為「各診間獨立預約清單」？'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingModeChange === 'SHARED_ROOMS' ? (
                <>
                  將以現有的一份設定作為共用範本：
                  <br />• 診所內所有診間都會共用這份預約清單
                  <br />• 診所內其他診間的獨立設定會被停用
                  <br />• 當日以後且未完成的預約會自動併入共用清單；歷史紀錄保留原 setting
                </>
              ) : (
                <>
                  將把共用設定複製到每個診間成為獨立版本：
                  <br />• 已派發的預約會跟著各自的診間
                  <br />• 若有「待派發」的預約必須先派發或取消，否則切換會失敗
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSwitching}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmModeChange} disabled={isSwitching}>
              {isSwitching ? '切換中…' : '確認切換'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
